import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
  ViewChild,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { BoschUiOptionBarModule } from '../../atoms/bosch-ui-option-bar/bosch-ui-option-bar.module';
import { BoschUiPaginatorComponent } from '../../atoms/bosch-ui-paginator/bosch-ui-paginator.component';
import { UI_PAGINATOR_PAGE_SIZE_OPTIONS } from './bosch-ui-complex-paginator.constant';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { BoschUiPageEvent } from '../../basics/bosch-ui-paginator/bosch-ui-paginator.model';
import { filter, pipe, startWith, Subject, takeUntil } from 'rxjs';
import { BoschUiPaginatorBase } from '../../basics/bosch-ui-paginator/bosch-ui-paginator-base';
import { TranslateModule } from '@ngx-translate/core';

@Component({
    selector: 'bosch-ui-complex-paginator',
    imports: [CommonModule, ReactiveFormsModule, BoschUiOptionBarModule, BoschUiPaginatorComponent, TranslateModule],
    templateUrl: './bosch-ui-complex-paginator.component.html',
    styleUrls: ['./bosch-ui-complex-paginator.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class BoschUiComplexPaginatorComponent extends BoschUiPaginatorBase implements OnInit, OnDestroy {
  @Input() label = 'common.uiKit.monitoring.label.paginatorPageSize';
  @Input() override set totalElements(totalElements: number) {
    this.totalElements$.next(totalElements);
    this.paginatorState = {
      ...this.paginatorState,
      totalElements,
    };
  }
  @Input() override set pageSize(pageSize: number) {
    this.pageSizeControl.setValue(pageSize);
    this.paginatorState = {
      ...this.paginatorState,
      pageSize,
    };
  }
  @Input() override set pageNumber(pageNumber: number) {
    this.pageNumber$.next(pageNumber);
    this.paginatorState = {
      ...this.paginatorState,
      pageNumber,
    };
  }
  @Input() pageSizeOptions: number[] = UI_PAGINATOR_PAGE_SIZE_OPTIONS;
  @Output() page = new EventEmitter<BoschUiPageEvent>();
  @ViewChild(BoschUiPaginatorComponent) paginator: BoschUiPaginatorComponent | null = null;

  protected pageSizeControl = new FormControl<number>(this.pageSizeOptions[0]);

  private _unsubscribe$ = new Subject<void>();

  ngOnInit(): void {
    this.initPageSizeSubscription();
  }

  ngOnDestroy(): void {
    this._unsubscribe$.next();
  }

  onPageChanges(event: BoschUiPageEvent): void {
    const pageSize = this.pageSizeControl.getRawValue();

    if (pageSize) {
      const state = this.getUpdatedPaginatorState(event.pageNumber, pageSize);
      this.page.next(state);
      this.page$.next(state);
    }
  }

  override nextPage(): void {
    if (this.paginator) {
      this.paginator.nextPage();
    }
  }

  override previousPage(): void {
    if (this.paginator) {
      this.paginator.previousPage();
    }
  }

  override firstPage(): void {
    if (this.paginator) {
      this.paginator.firstPage();
    }
  }

  override lastPage(): void {
    if (this.paginator) {
      this.paginator.lastPage();
    }
  }

  private getUpdatedPaginatorState(pageNumber: number, pageSize: number): BoschUiPageEvent {
    this.paginatorState = {
      ...this.paginatorState,
      previousPageNumber: this.paginatorState.previousPageNumber,
      pageNumber,
      pageSize,
    };

    return this.paginatorState;
  }

  private initPageSizeSubscription(): void {
    this.pageSizeControl.valueChanges
      .pipe(startWith(this.pageSizeControl.value), pipe(filter(Boolean)), takeUntil(this._unsubscribe$))
      .subscribe((pageSize: number) => this.pageSize$.next(pageSize));
  }
}
